import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Search,
  ChevronDown,
  Star,
  FileText,
  Wallet,
  Wrench,
  MessageCircle,
  Check,
  CalendarDays,
  Share2,
  Copy,
} from "lucide-react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
  AnimatePresence,
  type Variants,
} from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/lib/supabase";
import { getImageUrl } from "@/lib/imageUrl";
import { getListingAvailability } from "@/lib/roomAvailability";
import { SmoothImage } from "@/components/ui/smooth-image";
import room1 from "@/assets/room-1.jpg";
import room2 from "@/assets/room-2.jpg";
import room3 from "@/assets/room-3.jpg";
import {
  INK,
  INK_DEEP,
  MUTED,
  MUTED_SOFT,
  GREY,
  SAND,
  LINE,
  LINE_SOFT,
  IMAGE_BG,
  CHIP,
  EASE,
} from "@/lib/marketing-theme";
import heroRoom from "@/assets/room-4.webp";

/**
 * Home page, built to the approved website draft ("3a Start" in
 * `Website Drafts.dc.html`). Two rules from that draft's brief drive the
 * visuals and are easy to undo by accident:
 *   1. NO turquoise anywhere on the marketing site — the palette is petrol
 *      #092E2C, grey #9AAAA8 and the warm beige surfaces. (The turquoise
 *      tokens in tailwind.config belong to the deck/product UI.)
 *   2. Never name a city in a headline or claim; DACH expansion is planned.
 *      Cities appear only in filters and card metadata.
 * The draft itself is static — the motion below is this implementation's,
 * and every loop respects prefers-reduced-motion.
 */
const CARD_FALLBACKS = [room1, room2, room3];
/**
 * The draft's own room photography (public/assets, same files the website
 * draft ships). Used for the hero and the WG mosaic in place of the listing
 * photos: the database currently holds facade renders and aerials, which read
 * as an estate-agent listing rather than the rooms someone would live in.
 * Titles, prices, room counts and availability still come from the listings.
 */
const DRAFT_PHOTOS = [
  "/assets/room-muc-1.png",
  "/assets/room-aug-daimler.png",
  "/assets/room-z3.png",
  "/assets/room-tutzing.png",
  "/assets/room-z11.png",
  "/assets/room-z4.png",
];
const HERO_SLIDES = [
  "/assets/room-muc-1.png",
  "/assets/room-z3.png",
  "/assets/room-tutzing.png",
  "/assets/room-z11.png",
];
const HERO_INTERVAL_MS = 6000;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: EASE },
  }),
};

/** Softer, slightly blurred entrance for the editorial / media blocks. */
const revealBlur: Variants = {
  hidden: { opacity: 0, y: 38, filter: "blur(10px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.85, delay: i * 0.09, ease: EASE },
  }),
};

type Room = {
  id: number;
  title: string;
  propertyTitle: string;
  city: string;
  price: number;
  /** Effective listing availability (see lib/roomAvailability), not the raw `available` column. */
  available: boolean;
  availableFrom: string | null;
  roommates: number | null;
  image: string;
};

/** A property (WG) summarised from its public rooms for the mosaic. */
type Wg = {
  id: number;
  title: string;
  city: string;
  image: string;
  roomCount: number;
  freeCount: number;
  minPrice: number;
  /** Deep-links straight into a bookable room when the WG has one. */
  linkRoomId: number | null;
};

const FALLBACK: Record<string, Record<string, string>> = {
  en: {
    "hero.l1": "Find your",
    "hero.l2": "shared room.",
    "hero.sub": "View rooms, apply online and move in.",
    "hero.note": "All-in rent including utilities, internet and furniture.",
    "search.city": "City",
    "search.cityAny": "All cities",
    "search.date": "Move-in",
    "search.dateAny": "Today",
    "search.budget": "Budget",
    "search.budgetAny": "Any",
    "search.submit": "Search",
    "portals.label": "Our rooms are also listed on",
    "concept.heading1": "Coliving,",
    "concept.heading2": "made simple.",
    "concept.p1":
      "coliviq is coliving for everyone who would rather not start from zero in a new city. You move into a furnished room with your own lease — and you find company from day one.",
    "concept.p2":
      "We rent out and run our shared flats ourselves. From the first click to the key everything runs digitally, and whenever something breaks a message in the app is enough.",
    "wg.heading": "Our shared flats.",
    "wg.viewAll": "All rooms",
    "wg.free": "rooms available",
    "wg.freeOne": "room available",
    "wg.waitlist": "Waiting list",
    "wg.rooms": "rooms",
    "wg.from": "from",
    "wg.empty": "New shared flats are coming online soon.",
    "steps.heading": "This is how you move in.",
    "steps.step": "Step",
    "steps.s1.t": "Pick a room",
    "steps.s1.b":
      "Photos, floor plan, price and the available-from date for every room.",
    "steps.s2.t": "Apply online",
    "steps.s2.b":
      "Create a profile, upload your documents — one application for every shared flat.",
    "steps.s3.t": "Sign digitally",
    "steps.s3.b": "A single-room lease for your room, signed electronically.",
    "steps.s4.t": "Move in",
    "steps.s4.b":
      "Key handover and handover protocol, straight from inside the app.",
    "app.heading1": "Everything about living.",
    "app.heading2": "In one app.",
    "app.a1.t": "Contract and documents",
    "app.a1.b":
      "Lease, handover protocol and landlord confirmation — available any time.",
    "app.a1.c1k": "Document",
    "app.a1.c1v": "Contract signed",
    "app.a1.c2k": "Move-in",
    "app.a1.c2v": "01.11. · Room 2",
    "app.a2.t": "Rent and deposit",
    "app.a2.b":
      "Every payment in one view, rent by direct debit, deposit held separately.",
    "app.a2.c1k": "Payment",
    "app.a2.c1v": "Rent received",
    "app.a2.c2k": "Deposit",
    "app.a2.c2v": "Held",
    "app.a3.t": "Report an issue",
    "app.a3.b":
      "Report it with a photo and follow the status live — no chasing anyone by phone.",
    "app.a3.c1k": "Issue",
    "app.a3.c1v": "Heating · in progress",
    "app.a3.c2k": "Appointment",
    "app.a3.c2v": "Technician Thu, 10:00",
    "app.a4.t": "Community",
    "app.a4.b":
      "Flat chat, a city-wide chat across all shared flats, and events.",
    "app.a4.c1k": "Flat chat",
    "app.a4.c1v": "3 new messages",
    "app.a4.c2k": "Event",
    "app.a4.c2v": "Dinner on Friday",
    "app.a5.t": "Refer a friend",
    "app.a5.b":
      "Your code, your €100: when a friend signs a lease with it, the discount lands on your next rent.",
    "app.a5.c1k": "Referral",
    "app.a5.c1v": "Code copied",
    "app.a5.c2k": "Reward",
    "app.a5.c2v": "€100 credited",
    "reviews.heading": "Best ask the people who already live here.",
    "reviews.sub": "Voices from our residents.",
    "reviews.prev": "Previous reviews",
    "reviews.next": "More reviews",
    "split.owner.eyebrow": "For landlords",
    "split.owner.title":
      "You own a shared flat and would rather not manage it yourself?",
    "split.owner.cta": "To management",
    "split.partner.eyebrow": "For project partners",
    "split.partner.title": "Planning a project that could become coliving?",
    "split.partner.cta": "Propose a property",
    "cta.title": "Your room is already waiting. What are you waiting for?",
    "cta.button": "Find a room",
    "cta.secondary": "Write to us",
    "room.now": "Available now",
    "room.fromDate": "Available from",
    "room.warm": "warm",
    "room.view": "View",
    "room.flatmates": "flatmates",
    "room.onRequest": "Price on request",
    "phone.tab1": "Home",
    "phone.tab2": "Rent",
    "phone.tab3": "Issues",
    "phone.tab4": "Chat",
    "phone.yourRoom": "Your room",
    "phone.docs.1": "Lease",
    "phone.docs.2": "Handover protocol",
    "phone.docs.3": "Landlord confirmation",
    "phone.docs.signed": "Signed",
    "phone.rent.label": "All-in rent",
    "phone.rent.paid": "Paid",
    "phone.rent.deposit": "Deposit",
    "phone.rent.held": "Held",
    "phone.rent.next": "Next payment",
    "phone.rent.nextVal": "01.11. · SEPA",
    "phone.issue.title": "Heating stays cold",
    "phone.issue.status": "In progress",
    "phone.issue.s1": "Reported",
    "phone.issue.s2": "Appointment",
    "phone.issue.s3": "Done",
    "phone.issue.appt": "Technician Thu, 10:00",
    "phone.chat.1n": "Mara",
    "phone.chat.1": "Who is cooking tonight?",
    "phone.chat.2n": "You",
    "phone.chat.2": "I will bring dessert.",
    "phone.event.k": "Event",
    "phone.event.v": "Flat dinner · Friday 19:00",
    "phone.docs.all": "All documents",
    "phone.rent.history": "Last months",
    "phone.issue.past": "Kitchen light",
    "phone.issue.done": "Done",
    "phone.chat.typing": "Mara is typing",
    "phone.tab5": "Refer",
    "phone.ref.code": "Your referral code",
    "phone.ref.share": "Share now",
    "phone.ref.progress": "Successful referrals",
    "phone.ref.goal": "more and one month of rent is on us.",
    "phone.ref.successful": "Successful",
    "phone.ref.earned": "Earned so far",
  },
  de: {
    "hero.l1": "Finde dein",
    "hero.l2": "WG-Zimmer.",
    "hero.sub": "Zimmer ansehen, online bewerben und einziehen.",
    "hero.note": "Warmmiete inklusive Nebenkosten, Internet und Möbeln.",
    "search.city": "Stadt",
    "search.cityAny": "Alle Städte",
    "search.date": "Einzug",
    "search.dateAny": "Ab sofort",
    "search.budget": "Budget",
    "search.budgetAny": "Beliebig",
    "search.submit": "Suchen",
    "portals.label": "Unsere Zimmer findest du auch auf",
    "concept.heading1": "Coliving,",
    "concept.heading2": "einfach gemacht.",
    "concept.p1":
      "coliviq ist Coliving für alle, die in einer neuen Stadt nicht bei null anfangen wollen. Du ziehst in ein möbliertes Zimmer mit eigenem Mietvertrag — und findest vom ersten Tag an Anschluss.",
    "concept.p2":
      "Wir vermieten und betreiben unsere WGs selbst. Vom ersten Klick bis zum Schlüssel läuft alles digital, und wenn mal etwas klemmt, reicht eine Nachricht in der App.",
    "wg.heading": "Unsere WGs.",
    "wg.viewAll": "Alle Zimmer",
    "wg.free": "Zimmer verfügbar",
    "wg.freeOne": "Zimmer verfügbar",
    "wg.waitlist": "Warteliste",
    "wg.rooms": "Zimmer",
    "wg.from": "ab",
    "wg.empty": "Neue WGs sind bald online.",
    "steps.heading": "So ziehst du ein.",
    "steps.step": "Schritt",
    "steps.s1.t": "Zimmer aussuchen",
    "steps.s1.b":
      "Fotos, Grundriss, Preis und Verfügbarkeitsdatum für jedes Zimmer.",
    "steps.s2.t": "Online bewerben",
    "steps.s2.b":
      "Profil anlegen, Unterlagen hochladen — eine Bewerbung für alle WGs.",
    "steps.s3.t": "Digital unterschreiben",
    "steps.s3.b": "Einzelmietvertrag für dein Zimmer, per E-Signatur.",
    "steps.s4.t": "Einziehen",
    "steps.s4.b": "Schlüsselübergabe und Übergabeprotokoll, direkt in der App.",
    "app.heading1": "Alles rund ums Wohnen.",
    "app.heading2": "In einer App.",
    "app.a1.t": "Vertrag und Dokumente",
    "app.a1.b":
      "Mietvertrag, Übergabeprotokoll und Wohnungsgeberbestätigung — jederzeit abrufbar.",
    "app.a1.c1k": "Dokument",
    "app.a1.c1v": "Vertrag signiert",
    "app.a1.c2k": "Einzug",
    "app.a1.c2v": "01.11. · Zimmer 2",
    "app.a2.t": "Miete und Kaution",
    "app.a2.b":
      "Alle Zahlungen im Blick, Miete per Lastschrift, Kaution separat geführt.",
    "app.a2.c1k": "Zahlung",
    "app.a2.c1v": "Miete eingegangen",
    "app.a2.c2k": "Kaution",
    "app.a2.c2v": "Hinterlegt",
    "app.a3.t": "Anliegen melden",
    "app.a3.b":
      "Mit Foto melden, den Status live verfolgen — kein Hinterhertelefonieren.",
    "app.a3.c1k": "Anliegen",
    "app.a3.c1v": "Heizung · in Bearbeitung",
    "app.a3.c2k": "Termin",
    "app.a3.c2v": "Handwerker Do, 10 Uhr",
    "app.a4.t": "Community",
    "app.a4.b":
      "WG-Chat, Community-Chat mit allen WGs in deiner Stadt und Events.",
    "app.a4.c1k": "WG-Chat",
    "app.a4.c1v": "3 neue Nachrichten",
    "app.a4.c2k": "Event",
    "app.a4.c2v": "Dinner am Freitag",
    "app.a5.t": "Freunde empfehlen",
    "app.a5.b":
      "Dein Code, deine 100 €: unterschreibt jemand damit, landet der Rabatt auf deiner nächsten Miete.",
    "app.a5.c1k": "Empfehlung",
    "app.a5.c1v": "Code kopiert",
    "app.a5.c2k": "Prämie",
    "app.a5.c2v": "100 € gutgeschrieben",
    "reviews.heading": "Am besten fragst du die, die schon da wohnen.",
    "reviews.sub": "Stimmen unserer Bewohner.",
    "reviews.prev": "Vorherige Bewertungen",
    "reviews.next": "Weitere Bewertungen",
    "split.owner.eyebrow": "Für WG-Vermieter",
    "split.owner.title":
      "Sie haben eine WG und möchten sie nicht selbst verwalten?",
    "split.owner.cta": "Zur Verwaltung",
    "split.partner.eyebrow": "Für Projektpartner",
    "split.partner.title": "Sie planen ein Projekt, das Coliving werden kann?",
    "split.partner.cta": "Immobilie vorschlagen",
    "cta.title": "Dein Zimmer wartet schon. Worauf wartest du?",
    "cta.button": "Zimmer finden",
    "cta.secondary": "Schreib uns",
    "room.now": "Ab sofort verfügbar",
    "room.fromDate": "Verfügbar ab",
    "room.warm": "warm",
    "room.view": "Ansehen",
    "room.flatmates": "Mitbewohner",
    "room.onRequest": "Preis auf Anfrage",
    "phone.tab1": "Start",
    "phone.tab2": "Miete",
    "phone.tab3": "Anliegen",
    "phone.tab4": "Chat",
    "phone.yourRoom": "Dein Zimmer",
    "phone.docs.1": "Mietvertrag",
    "phone.docs.2": "Übergabeprotokoll",
    "phone.docs.3": "Wohnungsgeberbestätigung",
    "phone.docs.signed": "Signiert",
    "phone.rent.label": "Warmmiete",
    "phone.rent.paid": "Bezahlt",
    "phone.rent.deposit": "Kaution",
    "phone.rent.held": "Hinterlegt",
    "phone.rent.next": "Nächste Zahlung",
    "phone.rent.nextVal": "01.11. · SEPA",
    "phone.issue.title": "Heizung wird nicht warm",
    "phone.issue.status": "In Bearbeitung",
    "phone.issue.s1": "Gemeldet",
    "phone.issue.s2": "Termin",
    "phone.issue.s3": "Erledigt",
    "phone.issue.appt": "Handwerker Do, 10 Uhr",
    "phone.chat.1n": "Mara",
    "phone.chat.1": "Wer kocht heute Abend?",
    "phone.chat.2n": "Du",
    "phone.chat.2": "Ich bringe Nachtisch mit.",
    "phone.event.k": "Event",
    "phone.event.v": "WG-Dinner · Freitag 19 Uhr",
    "phone.docs.all": "Alle Dokumente",
    "phone.rent.history": "Letzte Monate",
    "phone.issue.past": "Küchenlampe",
    "phone.issue.done": "Erledigt",
    "phone.chat.typing": "Mara schreibt",
    "phone.tab5": "Empfehlen",
    "phone.ref.code": "Dein Empfehlungscode",
    "phone.ref.share": "Jetzt teilen",
    "phone.ref.progress": "Erfolgreiche Empfehlungen",
    "phone.ref.goal": "weitere: dann ist 1 Monat Miete geschenkt.",
    "phone.ref.successful": "Erfolgreich",
    "phone.ref.earned": "Bisher verdient",
  },
};

/** Portal names are brand wordmarks — not translated. */
const PORTALS = [
  { name: "ImmoScout24", weight: 700, tracking: "-0.02em" },
  { name: "WG-Gesucht", weight: 600, tracking: "-0.01em" },
  { name: "HousingAnywhere", weight: 600, tracking: "-0.03em" },
  { name: "wunderflats", weight: 700, tracking: "-0.04em" },
  { name: "kleinanzeigen", weight: 500, tracking: "-0.01em" },
];

const STEP_KEYS = ["s1", "s2", "s3", "s4"] as const;
const APP_KEYS = ["a1", "a2", "a3", "a4", "a5"] as const;
type AppKey = (typeof APP_KEYS)[number];

const APP_ICONS: Record<AppKey, typeof FileText> = {
  a1: FileText,
  a2: Wallet,
  a3: Wrench,
  a4: MessageCircle,
  a5: Share2,
};

/** Sample figures for the referral screen in the mock-up (not live data). */
const DEMO_REFERRAL = { code: "Coliviq-4821", done: 3, goal: 10, earned: 300 };

/** Mosaic row spans from the draft (6 cells over a 120px auto-row grid). */
const WG_SPANS = [5, 3, 4, 4, 3, 2];

const TESTIMONIALS: {
  name: string;
  avatar: string;
  when: { en: string; de: string };
  body: { en: string; de: string };
}[] = [
  {
    name: "Lena M.",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?fm=jpg&q=80&w=160&h=160&auto=format&fit=crop&crop=faces",
    when: { en: "March 2026", de: "März 2026" },
    body: {
      en: "Applied on Monday, contract on Wednesday, moved in on the first of the month. The room was completely furnished, I only had to unpack my suitcase.",
      de: "Bewerbung am Montag, Vertrag am Mittwoch, eingezogen am Monatsersten. Das Zimmer war komplett eingerichtet, ich musste nur meinen Koffer auspacken.",
    },
  },
  {
    name: "Sofia R.",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?fm=jpg&q=80&w=160&h=160&auto=format&fit=crop&crop=faces",
    when: { en: "February 2026", de: "Februar 2026" },
    body: {
      en: "I moved to a new city for work and knew nobody. Through the community events I had more contacts after two weeks than in the three years before.",
      de: "Ich bin für den Job in eine neue Stadt gezogen und kannte niemanden. Über die Community-Events hatte ich nach zwei Wochen mehr Kontakte als in drei Jahren davor.",
    },
  },
  {
    name: "Mara S.",
    avatar:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?fm=jpg&q=80&w=160&h=160&auto=format&fit=crop&crop=faces",
    when: { en: "April 2026", de: "April 2026" },
    body: {
      en: "Great flatmates, clean shared spaces and a team that is genuinely reachable.",
      de: "Tolle Mitbewohner, saubere Gemeinschaftsräume und ein Team, das wirklich erreichbar ist.",
    },
  },
  {
    name: "Jonas K.",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?fm=jpg&q=80&w=160&h=160&auto=format&fit=crop&crop=faces",
    when: { en: "January 2026", de: "Januar 2026" },
    body: {
      en: "Reported an issue through the app, somebody was there the next day.",
      de: "Anliegen über die App gemeldet, am nächsten Tag war jemand da.",
    },
  },
  {
    name: "David L.",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?fm=jpg&q=80&w=160&h=160&auto=format&fit=crop&crop=faces",
    when: { en: "November 2025", de: "November 2025" },
    body: {
      en: "My own lease instead of a sublet — that was the main reason for me. Everything transparent, no surprises on the utilities.",
      de: "Eigener Mietvertrag statt Untermiete — das war für mich der Hauptgrund. Alles transparent, keine Überraschungen bei den Nebenkosten.",
    },
  },
  {
    name: "Tim B.",
    avatar:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?fm=jpg&q=80&w=160&h=160&auto=format&fit=crop&crop=faces",
    when: { en: "December 2025", de: "Dezember 2025" },
    body: {
      en: "The deposit came back quickly after moving out and without any discussion. I would live with coliviq again any time.",
      de: "Die Kaution kam nach dem Auszug schnell und ohne Diskussion zurück. Würde jederzeit wieder bei coliviq wohnen.",
    },
  },
];

/* ------------------------------------------------------------------ */
/* Presentational pieces                                               */
/* ------------------------------------------------------------------ */

/** Scroll-triggered entrance wrapper — one place for the reveal timing. */
function Reveal({
  children,
  delay = 0,
  className,
  variants = fadeUp,
  amount = 0.3,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  variants?: Variants;
  amount?: number;
}) {
  return (
    <motion.div
      custom={delay}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Endlessly scrolling portal band. The track holds the list twice so the
 * -50% translation lands exactly on the duplicate and the loop is seamless;
 * it pauses on hover and stands still for reduced-motion visitors.
 */
function PortalMarquee({ label }: { label: string }) {
  const reduce = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const items = [...PORTALS, ...PORTALS];

  return (
    <div
      className="relative overflow-hidden py-7"
      style={{ background: INK }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span className="sr-only">{label}</span>
      {/* edge fades so wordmarks dissolve instead of being chopped off */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 sm:w-28"
        style={{ background: `linear-gradient(to right, ${INK}, transparent)` }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 sm:w-28"
        style={{ background: `linear-gradient(to left, ${INK}, transparent)` }}
      />
      <motion.div
        className="flex w-max items-center gap-14 sm:gap-24"
        animate={reduce || paused ? { x: 0 } : { x: ["0%", "-50%"] }}
        transition={
          reduce || paused
            ? { duration: 0 }
            : { duration: 30, ease: "linear", repeat: Infinity }
        }
      >
        {items.map((p, i) => (
          <span
            key={`${p.name}-${i}`}
            className="whitespace-nowrap text-white/85 transition-colors duration-300 hover:text-white"
            style={{
              fontSize: 17,
              fontWeight: p.weight,
              letterSpacing: p.tracking,
            }}
          >
            {p.name}
          </span>
        ))}
      </motion.div>
    </div>
  );
}

/**
 * Resident quotes as a carousel (embla for drag + keyboard). Autoplay stops
 * for good the moment the visitor touches it, so it never fights a swipe.
 */
function ReviewCarousel({
  isDe,
  prevLabel,
  nextLabel,
}: {
  isDe: boolean;
  prevLabel: string;
  nextLabel: string;
}) {
  const reduce = useReducedMotion();
  const [emblaRef, embla] = useEmblaCarousel({
    loop: true,
    align: "start",
    containScroll: "trimSnaps",
  });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (!embla) return;
    const sync = () => setSelected(embla.selectedScrollSnap());
    const reInit = () => {
      setSnaps(embla.scrollSnapList());
      sync();
    };
    reInit();
    embla.on("select", sync);
    embla.on("reInit", reInit);
    return () => {
      embla.off("select", sync);
      embla.off("reInit", reInit);
    };
  }, [embla]);

  useEffect(() => {
    if (!embla || reduce || held) return;
    const id = setInterval(() => embla.scrollNext(), 5200);
    return () => clearInterval(id);
  }, [embla, reduce, held]);

  const go = useCallback(
    (fn: "scrollPrev" | "scrollNext" | number) => {
      setHeld(true);
      if (typeof fn === "number") embla?.scrollTo(fn);
      else embla?.[fn]();
    },
    [embla]
  );

  return (
    <div onPointerDown={() => setHeld(true)} onMouseEnter={() => setHeld(true)}>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="-ml-5 flex">
          {TESTIMONIALS.map((item) => (
            <div
              key={item.name}
              className="min-w-0 shrink-0 grow-0 basis-full pl-5 sm:basis-1/2 lg:basis-1/3"
            >
              <motion.figure
                initial="rest"
                whileInView="in"
                whileHover="hover"
                viewport={{ once: true, amount: 0.4 }}
                variants={{
                  rest: { opacity: 0, y: 26 },
                  in: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.6,
                      ease: EASE,
                      staggerChildren: 0.05,
                      delayChildren: 0.12,
                    },
                  },
                  hover: {
                    y: -6,
                    boxShadow: "0 28px 56px -28px rgba(9,46,44,0.38)",
                    transition: { duration: 0.35, ease: EASE },
                  },
                }}
                className="flex h-full flex-col gap-6 rounded-[14px] border p-8"
                style={{ background: SAND, borderColor: LINE }}
              >
                <div className="flex gap-[3px]">
                  {/* the five stars land one after another, left to right */}
                  {Array.from({ length: 5 }).map((_, s) => (
                    <motion.span
                      key={s}
                      variants={{
                        rest: { opacity: 0, scale: 0.4 },
                        in: {
                          opacity: 1,
                          scale: 1,
                          transition: {
                            type: "spring",
                            stiffness: 480,
                            damping: 16,
                          },
                        },
                      }}
                    >
                      <Star
                        className="h-[15px] w-[15px]"
                        style={{ fill: INK, color: INK }}
                        strokeWidth={1.5}
                      />
                    </motion.span>
                  ))}
                </div>
                <blockquote
                  className="flex-1 text-base leading-[1.65]"
                  style={{ color: INK }}
                >
                  {isDe ? item.body.de : item.body.en}
                </blockquote>
                <figcaption className="flex items-center gap-3.5">
                  <img
                    src={item.avatar}
                    alt=""
                    loading="lazy"
                    className="h-11 w-11 shrink-0 rounded-full object-cover"
                    style={{ background: IMAGE_BG }}
                  />
                  <span>
                    <span
                      className="block text-[15.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {item.name}
                    </span>
                    <span
                      className="block text-[13.5px]"
                      style={{ color: MUTED_SOFT }}
                    >
                      {isDe ? item.when.de : item.when.en}
                    </span>
                  </span>
                </figcaption>
              </motion.figure>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-9 flex items-center justify-between gap-6">
        <div className="flex items-center gap-2">
          {snaps.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`${i + 1}`}
              aria-current={i === selected}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === selected ? 28 : 6,
                background: i === selected ? INK : "#C8D0CE",
              }}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => go("scrollPrev")}
            aria-label={prevLabel}
            className="flex h-12 w-12 items-center justify-center rounded-full border bg-white transition-all duration-300 hover:-translate-y-0.5"
            style={{ borderColor: LINE, color: INK }}
          >
            <ArrowRight className="h-4 w-4 rotate-180" strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={() => go("scrollNext")}
            aria-label={nextLabel}
            className="flex h-12 w-12 items-center justify-center rounded-full border bg-white transition-all duration-300 hover:-translate-y-0.5"
            style={{ borderColor: LINE, color: INK }}
          >
            <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The app mock-up inside the phone bezel. Every tab shows the visitor's
 * would-be room at the top (real data from the listing query) and then the
 * screen that row is about, so clicking a row visibly changes the phone.
 * Rows stagger in on each tab change.
 */
function PhoneScreen({
  tab,
  room,
  getT,
  isDe,
}: {
  tab: AppKey;
  room?: Room;
  getT: (key: string) => string;
  isDe: boolean;
}) {
  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
  };
  const item: Variants = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
  };

  const Row = ({
    children,
    className = "",
  }: {
    children: React.ReactNode;
    className?: string;
  }) => (
    <motion.div
      variants={item}
      className={`flex items-center gap-3 rounded-[12px] border bg-white px-3.5 py-3 ${className}`}
      style={{ borderColor: LINE }}
    >
      {children}
    </motion.div>
  );

  const label = (text: string) => (
    <span
      className="text-[9.5px] font-semibold uppercase tracking-[0.16em]"
      style={{ color: MUTED_SOFT }}
    >
      {text}
    </span>
  );

  return (
    <div className="absolute inset-0 flex flex-col pt-10">
      {/* room header — the same room the hero card shows */}
      <div className="px-4 pb-3">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="flex items-center gap-3 rounded-[14px] bg-white p-2.5 shadow-[0_8px_20px_rgba(9,46,44,0.06)]"
        >
          <SmoothImage
            src={DRAFT_PHOTOS[0]}
            alt=""
            onError={(e) => {
              e.currentTarget.src = CARD_FALLBACKS[0];
            }}
            wrapperClassName="h-11 w-11 shrink-0 rounded-[9px]"
            className="h-full w-full object-cover"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            {label(getT("phone.yourRoom"))}
            <span
              className="truncate text-[12.5px] font-semibold"
              style={{ color: INK }}
            >
              {room
                ? room.propertyTitle
                  ? `${room.propertyTitle} · ${room.title}`
                  : room.title
                : "coliviq"}
            </span>
          </span>
          {room && room.price > 0 && (
            <span
              className="shrink-0 whitespace-nowrap text-[12.5px] font-semibold"
              style={{ color: INK }}
            >
              {room.price} €
            </span>
          )}
        </motion.div>
      </div>

      {/* the part that swaps */}
      <div className="min-h-0 flex-1 overflow-hidden px-4">
        {/* Keyed, but deliberately NOT wrapped in AnimatePresence: with an
            exit animation the incoming screen only mounts once the outgoing
            one finishes, so a tab that is backgrounded (or otherwise not
            painting) mid-transition leaves the phone stuck on the old screen.
            Remounting on `tab` makes the swap unconditional. */}
        <div key={tab}>
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="flex flex-col gap-2.5"
          >
            {tab === "a1" &&
              ["1", "2", "3"].map((n) => (
                <Row key={n}>
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]"
                    style={{ background: CHIP }}
                  >
                    <FileText
                      className="h-3.5 w-3.5"
                      style={{ color: INK }}
                      strokeWidth={2}
                    />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className="truncate text-[11.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {getT(`phone.docs.${n}`)}
                    </span>
                    <span className="text-[10px]" style={{ color: MUTED_SOFT }}>
                      {getT("phone.docs.signed")}
                    </span>
                  </span>
                  <Check
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: INK }}
                    strokeWidth={2.5}
                  />
                </Row>
              ))}
            {tab === "a1" && (
              <motion.span
                variants={item}
                className="mt-1 flex items-center justify-center gap-1.5 rounded-[12px] border border-dashed py-2.5 text-[11px] font-semibold"
                style={{ borderColor: "#D5DDDC", color: MUTED }}
              >
                {getT("phone.docs.all")}
                <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
              </motion.span>
            )}

            {tab === "a2" && (
              <>
                <motion.div
                  variants={item}
                  className="flex flex-col gap-1 rounded-[12px] px-4 py-4"
                  style={{ background: INK }}
                >
                  <span
                    className="text-[9.5px] font-semibold uppercase tracking-[0.16em]"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {getT("phone.rent.label")}
                  </span>
                  <span
                    className="text-[26px] font-semibold text-white"
                    style={{ letterSpacing: "-0.03em" }}
                  >
                    {room && room.price > 0 ? `${room.price} €` : "—"}
                  </span>
                  <span
                    className="mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold"
                    style={{
                      background: "rgba(255,255,255,0.14)",
                      color: "#FFFFFF",
                    }}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                    {getT("phone.rent.paid")}
                  </span>
                </motion.div>
                <Row>
                  <span className="flex min-w-0 flex-1 flex-col">
                    {label(getT("phone.rent.deposit"))}
                    <span
                      className="text-[11.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {getT("phone.rent.held")}
                    </span>
                  </span>
                  <Wallet
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: MUTED }}
                    strokeWidth={2}
                  />
                </Row>
                <Row>
                  <span className="flex min-w-0 flex-1 flex-col">
                    {label(getT("phone.rent.next"))}
                    <span
                      className="text-[11.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {getT("phone.rent.nextVal")}
                    </span>
                  </span>
                  <CalendarDays
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: MUTED }}
                    strokeWidth={2}
                  />
                </Row>
                <motion.div
                  variants={item}
                  className="flex flex-col gap-2.5 rounded-[12px] border bg-white px-3.5 py-3"
                  style={{ borderColor: LINE }}
                >
                  {label(getT("phone.rent.history"))}
                  {/* six paid months, growing in from the baseline */}
                  <span className="flex h-10 items-end gap-1.5">
                    {[0.55, 0.7, 0.62, 0.85, 0.74, 1].map((h, bi) => (
                      <motion.span
                        key={bi}
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.25 + bi * 0.07,
                          ease: EASE,
                        }}
                        className="flex-1 origin-bottom rounded-[3px]"
                        style={{
                          height: `${h * 100}%`,
                          background: bi === 5 ? INK : "#CFD8D6",
                        }}
                      />
                    ))}
                  </span>
                </motion.div>
              </>
            )}

            {tab === "a3" && (
              <>
                <motion.div
                  variants={item}
                  className="flex flex-col gap-3 rounded-[12px] border bg-white p-3.5"
                  style={{ borderColor: LINE }}
                >
                  <span className="flex items-start gap-2.5">
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]"
                      style={{ background: CHIP }}
                    >
                      <Wrench
                        className="h-3.5 w-3.5"
                        style={{ color: INK }}
                        strokeWidth={2}
                      />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span
                        className="text-[11.5px] font-semibold"
                        style={{ color: INK }}
                      >
                        {getT("phone.issue.title")}
                      </span>
                      <span
                        className="text-[10px]"
                        style={{ color: MUTED_SOFT }}
                      >
                        {getT("phone.issue.status")}
                      </span>
                    </span>
                  </span>
                  {/* the progress rail fills as the issue moves along */}
                  <span className="flex items-center gap-1.5">
                    {[0, 1, 2].map((step) => (
                      <motion.span
                        key={step}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.25 + step * 0.14,
                          ease: EASE,
                        }}
                        className="h-1 flex-1 origin-left rounded-full"
                        style={{ background: step < 2 ? INK : "#D8DEDD" }}
                      />
                    ))}
                  </span>
                  <span className="flex justify-between">
                    {["s1", "s2", "s3"].map((k, si) => (
                      <span
                        key={k}
                        className="text-[9px] font-semibold uppercase tracking-[0.1em]"
                        style={{ color: si < 2 ? INK : MUTED_SOFT }}
                      >
                        {getT(`phone.issue.${k}`)}
                      </span>
                    ))}
                  </span>
                </motion.div>
                <Row>
                  <span className="flex min-w-0 flex-1 flex-col">
                    {label(getT("app.a3.c2k"))}
                    <span
                      className="truncate text-[11.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {getT("phone.issue.appt")}
                    </span>
                  </span>
                  <CalendarDays
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: MUTED }}
                    strokeWidth={2}
                  />
                </Row>
                <Row>
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]"
                    style={{ background: CHIP }}
                  >
                    <Check
                      className="h-3.5 w-3.5"
                      style={{ color: INK }}
                      strokeWidth={2.5}
                    />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className="truncate text-[11.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {getT("phone.issue.past")}
                    </span>
                    <span className="text-[10px]" style={{ color: MUTED_SOFT }}>
                      {getT("phone.issue.done")}
                    </span>
                  </span>
                </Row>
              </>
            )}

            {tab === "a4" && (
              <>
                {[
                  { n: "1", mine: false },
                  { n: "2", mine: true },
                ].map(({ n, mine }) => (
                  <motion.div
                    key={n}
                    variants={item}
                    className={`flex max-w-[86%] flex-col gap-1 rounded-[12px] px-3.5 py-2.5 ${
                      mine ? "self-end" : "self-start"
                    }`}
                    style={
                      mine
                        ? { background: INK }
                        : { background: "#FFFFFF", border: `1px solid ${LINE}` }
                    }
                  >
                    <span
                      className="text-[9.5px] font-semibold uppercase tracking-[0.14em]"
                      style={{
                        color: mine ? "rgba(255,255,255,0.55)" : MUTED_SOFT,
                      }}
                    >
                      {getT(`phone.chat.${n}n`)}
                    </span>
                    <span
                      className="text-[11.5px] leading-snug"
                      style={{ color: mine ? "#FFFFFF" : INK }}
                    >
                      {getT(`phone.chat.${n}`)}
                    </span>
                  </motion.div>
                ))}
                <motion.span
                  variants={item}
                  className="flex items-center gap-2 self-start rounded-[12px] border bg-white px-3 py-2"
                  style={{ borderColor: LINE }}
                >
                  {[0, 1, 2].map((d) => (
                    <motion.span
                      key={d}
                      animate={{ opacity: [0.25, 1, 0.25] }}
                      transition={{
                        duration: 1.1,
                        repeat: Infinity,
                        delay: d * 0.18,
                        ease: "easeInOut",
                      }}
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: INK }}
                    />
                  ))}
                  <span className="text-[10px]" style={{ color: MUTED_SOFT }}>
                    {getT("phone.chat.typing")}
                  </span>
                </motion.span>
                <Row className="mt-1">
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]"
                    style={{ background: CHIP }}
                  >
                    <CalendarDays
                      className="h-3.5 w-3.5"
                      style={{ color: INK }}
                      strokeWidth={2}
                    />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    {label(getT("phone.event.k"))}
                    <span
                      className="truncate text-[11.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {getT("phone.event.v")}
                    </span>
                  </span>
                </Row>
              </>
            )}
            {tab === "a5" && (
              <>
                {/* the code itself — the one thing a referrer copies */}
                <motion.div
                  variants={item}
                  className="flex flex-col gap-2.5 rounded-[12px] px-4 py-4"
                  style={{ background: INK }}
                >
                  <span
                    className="text-[9.5px] font-semibold uppercase tracking-[0.16em]"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {getT("phone.ref.code")}
                  </span>
                  <span className="flex items-center gap-2">
                    <span
                      className="text-[19px] font-semibold text-white"
                      style={{ letterSpacing: "-0.03em" }}
                    >
                      {DEMO_REFERRAL.code}
                    </span>
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-[7px]"
                      style={{ background: "rgba(255,255,255,0.14)" }}
                    >
                      <Copy className="h-3 w-3 text-white" strokeWidth={2} />
                    </span>
                  </span>
                  <span className="mt-1 flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-semibold text-white">
                      {DEMO_REFERRAL.done}
                      <span style={{ color: "rgba(255,255,255,0.55)" }}>
                        {" "}
                        / {DEMO_REFERRAL.goal}
                      </span>
                    </span>
                    <span
                      className="text-[9.5px]"
                      style={{ color: "rgba(255,255,255,0.55)" }}
                    >
                      {getT("phone.ref.progress")}
                    </span>
                  </span>
                  {/* ten pips, the earned ones filling left to right */}
                  <span className="flex gap-1">
                    {Array.from({ length: DEMO_REFERRAL.goal }, (_, pi) => (
                      <motion.span
                        key={pi}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{
                          duration: 0.35,
                          delay: 0.25 + pi * 0.05,
                          ease: EASE,
                        }}
                        className="h-1.5 flex-1 origin-left rounded-full"
                        style={{
                          background:
                            pi < DEMO_REFERRAL.done
                              ? "#FFFFFF"
                              : "rgba(255,255,255,0.2)",
                        }}
                      />
                    ))}
                  </span>
                  <span
                    className="text-[9.5px]"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {DEMO_REFERRAL.goal - DEMO_REFERRAL.done}{" "}
                    {getT("phone.ref.goal")}
                  </span>
                  <span
                    className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[10.5px] font-semibold"
                    style={{ color: INK }}
                  >
                    {getT("phone.ref.share")}
                    <Share2 className="h-3 w-3" strokeWidth={2.2} />
                  </span>
                </motion.div>
                <motion.div variants={item} className="flex gap-2.5">
                  {[
                    {
                      v: String(DEMO_REFERRAL.done),
                      l: getT("phone.ref.successful"),
                    },
                    {
                      v: `${DEMO_REFERRAL.earned} €`,
                      l: getT("phone.ref.earned"),
                    },
                  ].map((tile) => (
                    <span
                      key={tile.l}
                      className="flex flex-1 flex-col gap-0.5 rounded-[12px] border bg-white px-3.5 py-3"
                      style={{ borderColor: LINE }}
                    >
                      <span
                        className="text-[17px] font-semibold"
                        style={{ letterSpacing: "-0.03em", color: INK }}
                      >
                        {tile.v}
                      </span>
                      <span
                        className="text-[9.5px]"
                        style={{ color: MUTED_SOFT }}
                      >
                        {tile.l}
                      </span>
                    </span>
                  ))}
                </motion.div>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* tab bar — the pill slides to the active row */}
      <div
        className="mt-auto flex items-center justify-around border-t bg-white px-2 pb-4 pt-2.5"
        style={{ borderColor: LINE }}
      >
        {APP_KEYS.map((k, i) => {
          const Icon = APP_ICONS[k];
          const on = k === tab;
          return (
            <span
              key={k}
              className="relative flex flex-1 flex-col items-center gap-1 py-1"
            >
              {on && (
                <motion.span
                  layoutId="phone-tab"
                  transition={{ duration: 0.4, ease: EASE }}
                  className="absolute inset-x-1 inset-y-0 rounded-[10px]"
                  style={{ background: CHIP }}
                />
              )}
              <Icon
                className="relative h-3.5 w-3.5"
                style={{ color: on ? INK : "#B4BFBD" }}
                strokeWidth={2}
              />
              <span
                className="relative text-[8.5px] font-semibold"
                style={{ color: on ? INK : "#B4BFBD" }}
              >
                {getT(`phone.tab${i + 1}`)}
              </span>
            </span>
          );
        })}
      </div>
      <span className="sr-only">{isDe ? "App-Vorschau" : "App preview"}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */

const Home = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const isDe = language === "de";
  const reduce = useReducedMotion();

  const getT = (key: string): string => {
    const fromT = t(key)?.trim();
    if (fromT && fromT !== key) return fromT;
    return FALLBACK[language]?.[key] ?? FALLBACK.en[key] ?? key;
  };

  // Search bar
  const [city, setCity] = useState("");
  const [moveIn, setMoveIn] = useState("");
  const [budget, setBudget] = useState("");
  const [appTab, setAppTab] = useState(0);
  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const qs = new URLSearchParams();
    if (city) qs.set("city", city);
    if (budget) qs.set("budget", budget);
    if (moveIn) qs.set("movein", moveIn);
    const query = qs.toString();
    navigate(query ? `/rooms?${query}` : "/rooms");
  };

  // Hero parallax: the photo drifts slightly slower than the page scroll.
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImageY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const heroCopyY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  /**
   * One fetch feeds both the hero cards and the WG mosaic: the mosaic needs
   * every public room (a WG with nothing free is still shown, as a waiting
   * list), and the hero needs the subset that is free today.
   */
  const { data } = useQuery({
    queryKey: ["home-rooms-and-wgs"],
    queryFn: async (): Promise<{ rooms: Room[]; wgs: Wg[] }> => {
      const BASE_COLS = `id, title, price, available, available_from, is_hidden, images,
           properties ( id, title, city, image_url, is_hidden )`;
      // Columns some non-production projects still lack. Selecting a column a
      // project does not have fails the whole query, so a missing one is
      // dropped and the fetch retried rather than losing the section.
      const OPTIONAL_COLS = ["next_available_date", "roommates_count"];
      const fetchRooms = (cols: string[]) =>
        supabase
          .from("rooms")
          .select([BASE_COLS, ...cols].join(", "))
          .order("created_at", { ascending: false })
          .limit(400);
      let cols = [...OPTIONAL_COLS];
      let { data: rowsRaw, error } = await fetchRooms(cols);
      while (error) {
        const missing = cols.find((c) => error.message.includes(c));
        if (!missing) break;
        cols = cols.filter((c) => c !== missing);
        ({ data: rowsRaw, error } = await fetchRooms(cols));
      }
      if (error) throw error;
      const rows = (rowsRaw ?? []) as unknown as Array<{
        id: number;
        title: string;
        price: number | null;
        available: boolean | null;
        available_from?: string | null;
        next_available_date?: string | null;
        roommates_count?: number | null;
        is_hidden: boolean | null;
        images: unknown;
        properties: {
          id?: number | null;
          title?: string | null;
          city?: string | null;
          image_url?: string | null;
          is_hidden?: boolean | null;
        } | null;
      }>;
      const skip = new Set(["villa", "kitchen", "staircase"]);
      const visible = rows.filter(
        (r) =>
          r.is_hidden !== true &&
          r.properties?.is_hidden !== true &&
          !skip.has((r.title || "").trim().toLowerCase())
      );

      const rooms: Room[] = visible.map((r, i) => {
        const imgs = Array.isArray(r.images)
          ? (r.images.filter((x) => typeof x === "string") as string[])
          : [];
        const raw = imgs[0] || r.properties?.image_url || "";
        // Same rule as the listing + detail pages: a future availability date
        // (admin-owned `available_from`) wins over the raw `available` flag,
        // and a date that has arrived puts the room back on the market.
        const { listingAvailable } = getListingAvailability({
          available: r.available,
          available_from: r.available_from,
          next_available_date: r.next_available_date,
        });
        return {
          id: r.id,
          title: r.title || r.properties?.title || "Room",
          propertyTitle: r.properties?.title || "",
          city: r.properties?.city || "",
          price: Number(r.price) || 0,
          available: listingAvailable,
          availableFrom: r.available_from ?? r.next_available_date ?? null,
          roommates: r.roommates_count ?? null,
          image: raw
            ? (getImageUrl(raw, { width: 900 }) ?? raw)
            : CARD_FALLBACKS[i % 3],
        };
      });

      const byProperty = new Map<number, Wg>();
      visible.forEach((r, i) => {
        const pid = r.properties?.id;
        if (pid == null) return;
        const room = rooms[i];
        const existing = byProperty.get(pid);
        if (!existing) {
          const propImage = r.properties?.image_url;
          byProperty.set(pid, {
            id: pid,
            title: r.properties?.title || "WG",
            city: r.properties?.city || "",
            image: propImage
              ? (getImageUrl(propImage, { width: 1200 }) ?? propImage)
              : room.image,
            roomCount: 1,
            freeCount: room.available ? 1 : 0,
            minPrice: room.price > 0 ? room.price : 0,
            linkRoomId: room.available ? room.id : null,
          });
          return;
        }
        existing.roomCount += 1;
        if (room.available) {
          existing.freeCount += 1;
          existing.linkRoomId ??= room.id;
        }
        if (
          room.price > 0 &&
          (existing.minPrice === 0 || room.price < existing.minPrice)
        ) {
          existing.minPrice = room.price;
        }
      });

      return {
        // Hero cards showcase what someone can act on today, priced ones first.
        rooms: rooms
          .filter((r) => r.available)
          .sort(
            (a, b) =>
              (b.price > 0 ? 1 : 0) - (a.price > 0 ? 1 : 0) || b.price - a.price
          ),
        // WGs a visitor can move into first, then the biggest ones.
        wgs: [...byProperty.values()]
          .sort(
            (a, b) => b.freeCount - a.freeCount || b.roomCount - a.roomCount
          )
          .slice(0, 6),
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Memoised so the identity is stable across renders: a fresh [] each render
  // would re-run every effect and memo downstream of the listing data.
  const rooms = useMemo(() => data?.rooms ?? [], [data?.rooms]);
  const wgs = useMemo(() => data?.wgs ?? [], [data?.wgs]);
  const featureRoom = rooms[0];
  const thumbRooms = rooms.slice(1, 3);

  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(
      () => setHeroIdx((i) => (i + 1) % HERO_SLIDES.length),
      HERO_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [reduce]);

  /** "Verfügbar ab 01.11." / "Ab sofort verfügbar" — the draft's hero pill. */
  const freeLabel = (room: Room) => {
    if (!room.availableFrom) return getT("room.now");
    const d = new Date(room.availableFrom);
    if (Number.isNaN(d.getTime()) || d.getTime() <= Date.now())
      return getT("room.now");
    return `${getT("room.fromDate")} ${String(d.getDate()).padStart(2, "0")}.${String(
      d.getMonth() + 1
    ).padStart(2, "0")}.`;
  };

  const heroLines = [getT("hero.l1"), getT("hero.l2")];
  let wordCounter = 0;

  const activeApp = APP_KEYS[appTab];

  return (
    <div className="min-h-screen" style={{ background: SAND }}>
      {/* ===== HERO ===== */}
      <section
        ref={heroRef}
        className="px-5 pb-20 sm:px-8 lg:px-14"
        style={{ paddingTop: "calc(var(--hero-offset) + 3rem)" }}
      >
        <div className="mx-auto grid max-w-[1328px] items-center gap-12 min-[1400px]:grid-cols-2 min-[1400px]:gap-14">
          {/* ---- copy ---- */}
          <motion.div
            style={reduce ? undefined : { y: heroCopyY }}
            className="flex min-w-0 flex-col gap-8"
          >
            <div className="flex flex-col gap-[18px]">
              <h1
                className="font-display text-[2.75rem] font-semibold leading-[1.03] sm:text-[3.25rem] lg:text-[3.625rem]"
                style={{ letterSpacing: "-0.042em", color: INK }}
              >
                {heroLines.map((line, li) => (
                  // The reveal mask needs room for the descenders, or the
                  // tight leading clips the "y"/"g" tails. The negative margin
                  // cancels the padding so the line rhythm stays unchanged.
                  <span
                    key={li}
                    className="-mb-[0.18em] block overflow-hidden pb-[0.18em]"
                  >
                    {line.split(" ").map((word, wi, all) => {
                      const delay = 0.12 + wordCounter * 0.08;
                      wordCounter += 1;
                      return (
                        // The space is a real text node outside the animated
                        // span: a margin alone looks right but leaves the
                        // headline as one run-together word for screen
                        // readers and for anything reading the DOM text.
                        <span key={`${li}-${word}-${wordCounter}`}>
                          <motion.span
                            initial={{ y: "145%" }}
                            animate={{ y: "0%" }}
                            transition={{ duration: 0.9, delay, ease: EASE }}
                            className="inline-block"
                          >
                            {word}
                          </motion.span>
                          {wi < all.length - 1 ? " " : ""}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.42, ease: EASE }}
                className="max-w-[460px] text-[17px] leading-[1.55] sm:text-lg"
                style={{ color: MUTED }}
              >
                {getT("hero.sub")}
              </motion.p>
            </div>

            {/* ---- search ---- */}
            <motion.form
              onSubmit={onSearch}
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.55, ease: EASE }}
              className="flex flex-col gap-3 rounded-[24px] border bg-white p-4 shadow-[0_16px_40px_rgba(9,46,44,0.08)] sm:h-[72px] sm:grid sm:grid-cols-[1.2fr_1fr_1fr_auto] sm:items-stretch sm:gap-0 sm:rounded-full sm:p-2 sm:pl-0"
              style={{ borderColor: LINE }}
            >
              <label className="flex min-w-0 flex-col justify-center gap-[3px] px-5">
                <span
                  className="text-[12.5px] font-semibold"
                  style={{ color: MUTED_SOFT }}
                >
                  {getT("search.city")}
                </span>
                <span className="flex items-center justify-between gap-2">
                  <select
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full cursor-pointer appearance-none truncate bg-transparent text-[15.5px] font-semibold outline-none"
                    style={{ color: INK }}
                  >
                    <option value="">{getT("search.cityAny")}</option>
                    <option value="Berlin">
                      {t("public.home.cityBerlin", "Berlin")}
                    </option>
                    <option value="München">
                      {t("admin.city.munich", "München")}
                    </option>
                    <option value="Augsburg">
                      {t("admin.city.augsburg", "Augsburg")}
                    </option>
                    <option value="Tutzing">
                      {t("public.home.cityTutzing", "Tutzing")}
                    </option>
                  </select>
                  <ChevronDown
                    className="pointer-events-none h-3.5 w-3.5 shrink-0"
                    style={{ color: MUTED }}
                    strokeWidth={2.5}
                  />
                </span>
              </label>

              <label
                className="flex min-w-0 flex-col justify-center gap-[3px] px-5 sm:border-l"
                style={{ borderColor: LINE_SOFT }}
              >
                <span
                  className="text-[12.5px] font-semibold"
                  style={{ color: MUTED_SOFT }}
                >
                  {getT("search.date")}
                </span>
                {/* A native date input cannot carry a placeholder, and an
                    empty one renders the browser's "dd.mm.yyyy". The default
                    the draft asks for is a word, so the words sit on top and
                    the (still fully clickable) input is hidden until a date is
                    actually chosen — including its calendar button, which is
                    why one is drawn here instead. */}
                <span className="relative block">
                  <input
                    type="date"
                    aria-label={getT("search.date")}
                    value={moveIn}
                    onChange={(e) => setMoveIn(e.target.value)}
                    className={`w-full bg-transparent text-[15.5px] font-semibold outline-none ${
                      moveIn ? "" : "opacity-0"
                    }`}
                    style={{ color: INK }}
                  />
                  {!moveIn && (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 flex items-center justify-between gap-2 text-[15.5px] font-semibold"
                      style={{ color: INK }}
                    >
                      {getT("search.dateAny")}
                      <CalendarDays
                        className="h-4 w-4 shrink-0"
                        strokeWidth={1.8}
                        style={{ color: MUTED_SOFT }}
                      />
                    </span>
                  )}
                </span>
              </label>

              <label
                className="flex min-w-0 flex-col justify-center gap-[3px] px-5 sm:border-l"
                style={{ borderColor: LINE_SOFT }}
              >
                <span
                  className="text-[12.5px] font-semibold"
                  style={{ color: MUTED_SOFT }}
                >
                  {getT("search.budget")}
                </span>
                <span className="flex items-center justify-between gap-2">
                  <select
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    className="w-full cursor-pointer appearance-none truncate bg-transparent text-[15.5px] font-semibold outline-none"
                    style={{ color: INK }}
                  >
                    <option value="">{getT("search.budgetAny")}</option>
                    <option value="500">
                      {isDe ? "bis 500 €" : "up to €500"}
                    </option>
                    <option value="700">
                      {isDe ? "bis 700 €" : "up to €700"}
                    </option>
                    <option value="900">
                      {isDe ? "bis 900 €" : "up to €900"}
                    </option>
                    <option value="1200">
                      {isDe ? "bis 1200 €" : "up to €1200"}
                    </option>
                  </select>
                  <ChevronDown
                    className="pointer-events-none h-3.5 w-3.5 shrink-0"
                    style={{ color: MUTED }}
                    strokeWidth={2.5}
                  />
                </span>
              </label>

              <button
                type="submit"
                className="inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-full px-6 text-[15px] font-semibold text-white transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: INK }}
              >
                <Search className="h-4 w-4" strokeWidth={2.5} />
                {getT("search.submit")}
              </button>
            </motion.form>

            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
              className="text-sm"
              style={{ color: MUTED_SOFT }}
            >
              {getT("hero.note")}
            </motion.span>
          </motion.div>

          {/* ---- media ---- */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.15, ease: EASE }}
            className="relative min-w-0 min-[1400px]:h-[540px]"
          >
            <div
              className="relative aspect-[4/3] overflow-hidden rounded-[14px] min-[1400px]:absolute min-[1400px]:inset-0 min-[1400px]:aspect-auto"
              style={{ background: IMAGE_BG }}
            >
              <motion.div
                style={reduce ? undefined : { y: heroImageY }}
                className="absolute inset-0"
              >
                {HERO_SLIDES.map((src, i) => (
                  <motion.img
                    key={src}
                    src={src}
                    onError={(e) => {
                      e.currentTarget.src = heroRoom;
                    }}
                    alt={isDe ? "coliviq WG-Zimmer" : "coliviq shared room"}
                    initial={false}
                    animate={{
                      opacity: i === heroIdx ? 1 : 0,
                      // the visible slide drifts in slowly; the others sit
                      // pre-zoomed so the next crossfade starts wide again
                      scale: i === heroIdx ? 1.02 : 1.1,
                    }}
                    transition={{ duration: 1.3, ease: EASE }}
                    className="absolute inset-0 h-full w-full object-cover object-center"
                  />
                ))}
              </motion.div>

              {HERO_SLIDES.length > 1 && (
                <div className="absolute bottom-4 right-4 z-30 flex items-center gap-1.5 rounded-full bg-black/25 px-2.5 py-2 backdrop-blur-sm">
                  {HERO_SLIDES.map((src, i) => (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setHeroIdx(i)}
                      aria-label={
                        isDe ? `Bild ${i + 1} anzeigen` : `Show image ${i + 1}`
                      }
                      aria-current={i === heroIdx}
                      className="h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: i === heroIdx ? 22 : 6,
                        background:
                          i === heroIdx ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* room thumbnails, top right */}
            <div className="absolute right-4 top-4 z-20 hidden w-[280px] flex-col gap-2.5 sm:flex lg:right-6 lg:top-6">
              {thumbRooms.map((room, i) => (
                <motion.div
                  key={room.id}
                  initial={{ opacity: 0, y: 14, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.7 + i * 0.12,
                    ease: EASE,
                  }}
                >
                  <motion.div
                    animate={reduce ? undefined : { y: [0, -6, 0] }}
                    transition={{
                      duration: 5 + i,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <Link
                      to={`/property/${room.id}`}
                      className="flex items-center gap-3.5 rounded-[14px] border bg-white p-2.5 transition-transform duration-300 hover:scale-[1.03]"
                      style={{ borderColor: LINE }}
                    >
                      <SmoothImage
                        src={room.image}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.src = CARD_FALLBACKS[i % 3];
                        }}
                        wrapperClassName="h-16 w-16 shrink-0 rounded-[10px]"
                        className="h-full w-full object-cover"
                      />
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span
                          className="truncate text-[14.5px] font-semibold"
                          style={{ color: INK }}
                        >
                          {room.propertyTitle || room.title}
                        </span>
                        <span
                          className="truncate text-[13px]"
                          style={{ color: MUTED_SOFT }}
                        >
                          {room.propertyTitle ? room.title : room.city}
                        </span>
                      </span>
                      {room.price > 0 && (
                        <span
                          className="ml-auto whitespace-nowrap text-[14.5px] font-semibold"
                          style={{ color: INK }}
                        >
                          {room.price} €
                        </span>
                      )}
                    </Link>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            {/* featured room card, bottom left */}
            {featureRoom && (
              <motion.div
                initial={{ opacity: 0, y: 26, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                className="relative z-20 mx-3 -mt-12 sm:absolute sm:bottom-4 sm:left-4 sm:mx-0 sm:mt-0 sm:w-[300px] lg:bottom-6 lg:left-6"
              >
                {/* The idle drift lives on its own wrapper so the hover lift
                    below can own `y` without the two fighting each other. */}
                <motion.div
                  animate={reduce ? undefined : { y: [0, -9, 0] }}
                  transition={{
                    duration: 6.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{ willChange: "transform" }}
                >
                  <motion.div
                    variants={{
                      rest: {
                        y: 0,
                        boxShadow: "0 24px 48px rgba(6,32,31,0.22)",
                      },
                      hover: {
                        y: -8,
                        boxShadow: "0 40px 72px rgba(6,32,31,0.34)",
                      },
                    }}
                    initial="rest"
                    whileHover={reduce ? undefined : "hover"}
                    animate="rest"
                    transition={{ duration: 0.45, ease: EASE }}
                    className="group/card flex flex-col gap-3.5 rounded-[14px] bg-white p-5"
                  >
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      variants={{
                        hidden: {},
                        visible: {
                          transition: {
                            staggerChildren: 0.08,
                            delayChildren: 1,
                          },
                        },
                      }}
                      className="flex flex-col gap-3.5"
                    >
                      <motion.div
                        variants={{
                          hidden: { opacity: 0, y: 8 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="flex items-center justify-between gap-2.5"
                      >
                        <span
                          className="inline-flex h-[26px] items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold"
                          style={{ background: CHIP, color: INK }}
                        >
                          {/* live dot: this listing is bookable right now */}
                          <span className="relative flex h-1.5 w-1.5">
                            {!reduce && (
                              <motion.span
                                animate={{ scale: [1, 2.4], opacity: [0.6, 0] }}
                                transition={{
                                  duration: 1.8,
                                  repeat: Infinity,
                                  ease: "easeOut",
                                }}
                                className="absolute inset-0 rounded-full"
                                style={{ background: INK }}
                              />
                            )}
                            <span
                              className="relative h-1.5 w-1.5 rounded-full"
                              style={{ background: INK }}
                            />
                          </span>
                          {freeLabel(featureRoom)}
                        </span>
                        {featureRoom.city && (
                          <span
                            className="text-[13px]"
                            style={{ color: MUTED_SOFT }}
                          >
                            {featureRoom.city}
                          </span>
                        )}
                      </motion.div>

                      <motion.div
                        variants={{
                          hidden: { opacity: 0, y: 8 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="flex flex-col gap-0.5"
                      >
                        <span
                          className="text-[18px] font-semibold"
                          style={{ letterSpacing: "-0.02em", color: INK }}
                        >
                          {featureRoom.propertyTitle
                            ? `${featureRoom.propertyTitle} · ${featureRoom.title}`
                            : featureRoom.title}
                        </span>
                        {featureRoom.roommates != null && (
                          <span
                            className="text-sm"
                            style={{ color: MUTED_SOFT }}
                          >
                            {featureRoom.roommates} {getT("room.flatmates")}
                          </span>
                        )}
                      </motion.div>

                      <motion.div
                        variants={{
                          hidden: { opacity: 0, y: 8 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="flex items-center justify-between gap-3 border-t pt-3.5"
                        style={{ borderColor: LINE_SOFT }}
                      >
                        {featureRoom.price > 0 ? (
                          <span
                            className="text-[20px] font-semibold"
                            style={{ letterSpacing: "-0.03em", color: INK }}
                          >
                            {getT("wg.from")} {featureRoom.price} €
                            <span
                              className="text-[13px] font-medium"
                              style={{ color: MUTED_SOFT }}
                            >
                              {" "}
                              {getT("room.warm")}
                            </span>
                          </span>
                        ) : (
                          <span
                            className="text-[15px] font-semibold"
                            style={{ color: MUTED }}
                          >
                            {getT("room.onRequest")}
                          </span>
                        )}
                        <Link
                          to={`/property/${featureRoom.id}`}
                          className="inline-flex h-[38px] shrink-0 items-center gap-0 rounded-full px-4 text-sm font-semibold text-white transition-all duration-300 hover:gap-1.5"
                          style={{ background: INK }}
                        >
                          {getT("room.view")}
                          {/* the arrow unfurls instead of popping in, so the
                              pill grows smoothly rather than jumping width */}
                          <ArrowRight
                            className="h-4 w-0 opacity-0 transition-all duration-300 group-hover/card:w-4 group-hover/card:opacity-100"
                            strokeWidth={2.5}
                          />
                        </Link>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ===== PORTAL BAND ===== */}
      <PortalMarquee label={getT("portals.label")} />

      {/* ===== COLIVING, MADE SIMPLE ===== */}
      <section className="bg-white px-5 py-24 sm:px-8 lg:px-14">
        <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[0.7fr_1.3fr] lg:gap-14">
          <Reveal variants={revealBlur} amount={0.4}>
            <h2
              className="font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
              style={{ letterSpacing: "-0.04em", color: INK }}
            >
              {getT("concept.heading1")}
              <br />
              {getT("concept.heading2")}
            </h2>
          </Reveal>
          <Reveal variants={revealBlur} delay={1} amount={0.3}>
            <div className="flex flex-col gap-5">
              <p
                className="max-w-[680px] text-[17px] leading-[1.6] sm:text-[19px]"
                style={{ color: INK }}
              >
                {getT("concept.p1")}
              </p>
              <p
                className="max-w-[680px] text-[17px] leading-[1.6] sm:text-[19px]"
                style={{ color: MUTED }}
              >
                {getT("concept.p2")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== OUR SHARED FLATS (mosaic) ===== */}
      <section
        className="px-5 py-24 sm:px-8 lg:px-14"
        style={{ background: SAND }}
      >
        <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal amount={0.6}>
              <h2
                className="font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
                style={{ letterSpacing: "-0.04em", color: INK }}
              >
                {getT("wg.heading")}
              </h2>
            </Reveal>
            <Link
              to="/rooms"
              className="group inline-flex items-center gap-2.5 text-base font-semibold"
              style={{ color: INK }}
            >
              {getT("wg.viewAll")}
              <ArrowRight
                className="h-[17px] w-[17px] transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2.4}
              />
            </Link>
          </div>

          {wgs.length === 0 ? (
            <div
              className="rounded-[14px] border bg-white p-16 text-center"
              style={{ borderColor: LINE, color: MUTED }}
            >
              {getT("wg.empty")}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:auto-rows-[120px] xl:grid-flow-dense xl:grid-cols-3">
              {wgs.map((wg, i) => (
                <motion.div
                  key={wg.id}
                  custom={i % 3}
                  variants={revealBlur}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  // The draft's staggered spans are a three-column idea: as
                  // inline `gridRow` they also applied to the one- and
                  // two-column layouts, where a span of 5 became a 660px-tall
                  // card on a phone. Carried as a custom property so the span
                  // only takes effect once the mosaic actually has 3 columns.
                  style={
                    {
                      "--wg-span": WG_SPANS[i % WG_SPANS.length],
                    } as React.CSSProperties
                  }
                  className="h-[260px] sm:h-[300px] xl:h-auto xl:[grid-row:span_var(--wg-span)]"
                >
                  <Link
                    to={
                      wg.linkRoomId
                        ? `/property/${wg.linkRoomId}`
                        : `/rooms?city=${encodeURIComponent(wg.city)}`
                    }
                    className="group relative block h-full overflow-hidden rounded-[14px] transition-transform duration-500 hover:-translate-y-1"
                    style={{ background: IMAGE_BG }}
                  >
                    <SmoothImage
                      src={DRAFT_PHOTOS[i % DRAFT_PHOTOS.length]}
                      alt={wg.title}
                      onError={(e) => {
                        e.currentTarget.src = CARD_FALLBACKS[i % 3];
                      }}
                      wrapperClassName="absolute inset-0 h-full w-full"
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 transition-opacity duration-500 group-hover:opacity-80"
                      style={{ background: "rgba(6,32,31,0.28)" }}
                    />
                    <span
                      className="absolute left-7 top-7 inline-flex h-[30px] items-center rounded-full px-3.5 text-[13.5px] font-semibold"
                      style={
                        wg.freeCount > 0
                          ? { background: "#FFFFFF", color: INK }
                          : {
                              background: "rgba(255,255,255,0.72)",
                              color: MUTED,
                            }
                      }
                    >
                      {wg.freeCount > 0
                        ? `${wg.freeCount} ${
                            wg.freeCount === 1
                              ? getT("wg.freeOne")
                              : getT("wg.free")
                          }`
                        : getT("wg.waitlist")}
                    </span>
                    <span className="absolute right-6 top-6 flex h-9 w-9 translate-y-2 items-center justify-center rounded-full bg-white opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                      <ArrowRight
                        className="h-4 w-4 -rotate-45"
                        style={{ color: INK }}
                        strokeWidth={2.4}
                      />
                    </span>
                    <div className="pointer-events-none absolute inset-x-7 bottom-10 flex flex-col gap-1">
                      <span
                        className="font-display text-2xl font-semibold leading-[1.1] text-white transition-transform duration-500 group-hover:-translate-y-0.5"
                        style={{ letterSpacing: "-0.03em" }}
                      >
                        {wg.title}
                      </span>
                      <span className="text-[15px] text-white/90">
                        {[
                          wg.city,
                          `${wg.roomCount} ${getT("wg.rooms")}`,
                          wg.minPrice > 0
                            ? `${getT("wg.from")} ${wg.minPrice} €`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== HOW YOU MOVE IN ===== */}
      <section className="bg-white px-5 py-24 sm:px-8 lg:px-14">
        <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
          <Reveal amount={0.6}>
            <h2
              className="font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
              style={{ letterSpacing: "-0.04em", color: INK }}
            >
              {getT("steps.heading")}
            </h2>
          </Reveal>
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
            {STEP_KEYS.map((k, i) => (
              <motion.div
                key={k}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                className="flex min-w-0 flex-col gap-2"
              >
                {/* the 1.5px rule draws itself in, column by column */}
                <motion.span
                  aria-hidden
                  variants={{
                    hidden: { scaleX: 0 },
                    visible: {
                      scaleX: 1,
                      transition: {
                        duration: 0.7,
                        delay: i * 0.12,
                        ease: EASE,
                      },
                    },
                  }}
                  className="mb-3 block h-[1.5px] w-full origin-left"
                  style={{ background: INK }}
                />
                <motion.span
                  variants={fadeUp}
                  custom={i}
                  className="text-[13px] font-semibold"
                  style={{ color: MUTED_SOFT }}
                >
                  {getT("steps.step")} {String(i + 1).padStart(2, "0")}
                </motion.span>
                <motion.span
                  variants={fadeUp}
                  custom={i + 0.5}
                  className="text-[19px] font-semibold"
                  style={{ letterSpacing: "-0.02em", color: INK }}
                >
                  {getT(`steps.${k}.t`)}
                </motion.span>
                <motion.span
                  variants={fadeUp}
                  custom={i + 1}
                  className="text-[15.5px] leading-[1.55]"
                  style={{ color: MUTED }}
                >
                  {getT(`steps.${k}.b`)}
                </motion.span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== EVERYTHING IN ONE APP ===== */}
      <section
        className="grid xl:grid-cols-2"
        style={{ background: INK }}
        id="app"
      >
        <div className="flex flex-col justify-center gap-7 px-5 py-24 sm:px-8 lg:px-14">
          <Reveal variants={revealBlur} amount={0.4}>
            <h2
              className="font-display text-[2rem] font-semibold leading-[1.06] text-white sm:text-[2.5rem]"
              style={{ letterSpacing: "-0.04em" }}
            >
              {getT("app.heading1")}
              <br />
              {getT("app.heading2")}
            </h2>
          </Reveal>
          <div className="flex flex-col">
            {APP_KEYS.map((k, i) => {
              const on = appTab === i;
              return (
                <Reveal key={k} delay={i} amount={0.2}>
                  <button
                    type="button"
                    onClick={() => setAppTab(i)}
                    aria-expanded={on}
                    className="flex w-full flex-col gap-1.5 border-t py-[18px] text-left transition-colors"
                    style={{ borderColor: "rgba(255,255,255,0.16)" }}
                  >
                    <span
                      className="text-[19px] font-semibold transition-colors duration-300"
                      style={{
                        letterSpacing: "-0.02em",
                        color: on ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                      }}
                    >
                      {getT(`app.${k}.t`)}
                    </span>
                    <AnimatePresence initial={false}>
                      {on && (
                        <motion.span
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: EASE }}
                          className="block overflow-hidden"
                        >
                          <span
                            className="block max-w-[440px] pt-1.5 text-[15.5px] leading-[1.55]"
                            style={{ color: "rgba(247,243,236,0.82)" }}
                          >
                            {getT(`app.${k}.b`)}
                          </span>
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* phone — the screen changes with the selected row */}
        <div className="relative flex items-center justify-center overflow-hidden px-8 pb-24 sm:px-12 xl:px-0 xl:py-24">
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-[-120px] left-1/2 h-[560px] w-[560px] -translate-x-1/2 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(154,170,168,0.22), rgba(154,170,168,0))",
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 1, ease: EASE }}
            className="relative w-[248px] sm:w-[300px] xl:w-[380px]"
          >
            <motion.div
              animate={reduce ? undefined : { y: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-[46px] p-2.5 shadow-[0_60px_120px_rgba(3,18,17,0.6),0_0_0_1px_rgba(255,255,255,0.06)] sm:rounded-[56px] sm:p-3"
              style={{ background: INK_DEEP }}
            >
              <div
                className="relative aspect-[380/730] overflow-hidden rounded-[38px] sm:rounded-[46px]"
                style={{ background: SAND }}
              >
                {/* notch */}
                <div
                  aria-hidden
                  className="absolute left-1/2 top-2.5 z-20 h-5 w-20 -translate-x-1/2 rounded-full sm:w-24"
                  style={{ background: INK_DEEP }}
                />
                <PhoneScreen
                  tab={activeApp}
                  room={featureRoom}
                  getT={getT}
                  isDe={isDe}
                />
              </div>
            </motion.div>

            {/* the two chips swap with the selected row */}
            <AnimatePresence mode="popLayout">
              {(["c1", "c2"] as const).map((slot, i) => (
                <motion.div
                  key={`${activeApp}-${slot}`}
                  initial={{ opacity: 0, y: 16, scale: 0.94 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.96 }}
                  transition={{ duration: 0.45, delay: i * 0.08, ease: EASE }}
                  className={`absolute z-20 hidden rounded-[14px] bg-white px-4 py-3 shadow-[0_24px_48px_rgba(3,18,17,0.35)] sm:block ${
                    i === 0
                      ? "-left-14 top-28 xl:-left-24"
                      : "-right-14 bottom-28 xl:-right-24"
                  }`}
                >
                  <motion.span
                    animate={
                      reduce ? undefined : { y: [0, i === 0 ? -6 : 6, 0] }
                    }
                    transition={{
                      duration: 5 + i,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="flex items-center gap-3"
                  >
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]"
                      style={{ background: CHIP }}
                    >
                      {(() => {
                        const Icon = APP_ICONS[activeApp];
                        return (
                          <Icon
                            className="h-4 w-4"
                            style={{ color: INK }}
                            strokeWidth={2}
                          />
                        );
                      })()}
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span
                        className="text-[10.5px] font-semibold uppercase tracking-[0.14em]"
                        style={{ color: MUTED_SOFT }}
                      >
                        {getT(`app.${activeApp}.${slot}k`)}
                      </span>
                      <span
                        className="whitespace-nowrap text-[13.5px] font-semibold"
                        style={{ color: INK }}
                      >
                        {getT(`app.${activeApp}.${slot}v`)}
                      </span>
                    </span>
                  </motion.span>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* ===== RESIDENT REVIEWS (carousel) ===== */}
      <section className="bg-white px-5 py-24 sm:px-8 lg:px-14">
        <div className="mx-auto flex max-w-[1328px] flex-col gap-14">
          <Reveal amount={0.5}>
            <div className="flex flex-col gap-1.5">
              <h2
                className="max-w-4xl font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
                style={{ letterSpacing: "-0.04em", color: INK }}
              >
                {getT("reviews.heading")}
              </h2>
              <span
                className="font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
                style={{ letterSpacing: "-0.04em", color: GREY }}
              >
                {getT("reviews.sub")}
              </span>
            </div>
          </Reveal>
          <Reveal amount={0.15}>
            <ReviewCarousel
              isDe={isDe}
              prevLabel={getT("reviews.prev")}
              nextLabel={getT("reviews.next")}
            />
          </Reveal>
        </div>
      </section>

      {/* ===== LANDLORDS / PROJECT PARTNERS ===== */}
      <section
        className="px-5 py-24 sm:px-8 lg:px-14"
        style={{ background: SAND }}
      >
        <div className="mx-auto grid max-w-[1328px] gap-4 md:grid-cols-2">
          {[
            {
              // COL-464 left /owners serving the management pitch (Operators)
              // and /developers the development one (Owners). Both cards
              // pointed at /developers, so "Für WG-Vermieter" opened the
              // Projektpartner page — the same mix-up the footer had.
              to: "/owners",
              image: "/assets/own-hero-green-v2.jpg",
              eyebrow: getT("split.owner.eyebrow"),
              title: getT("split.owner.title"),
              cta: getT("split.owner.cta"),
            },
            {
              to: "/developers",
              image: "/assets/projektpartner-muenchen.png",
              eyebrow: getT("split.partner.eyebrow"),
              title: getT("split.partner.title"),
              cta: getT("split.partner.cta"),
            },
          ].map((card, i) => (
            // Keyed on the image, not the route: two cards sharing a
            // destination is what made React warn about duplicate keys here.
            <Reveal key={card.image} delay={i} amount={0.25}>
              <Link
                to={card.to}
                className="group relative block h-[320px] overflow-hidden rounded-[14px] sm:h-[420px]"
                style={{ background: IMAGE_BG }}
              >
                <SmoothImage
                  src={card.image}
                  alt=""
                  wrapperClassName="absolute inset-0 h-full w-full"
                  className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{ background: "rgba(6,32,31,0.34)" }}
                />
                <div className="pointer-events-none absolute inset-x-8 bottom-12 flex flex-col gap-3">
                  <span className="text-sm font-semibold text-white">
                    {card.eyebrow}
                  </span>
                  <span
                    className="max-w-[440px] font-display text-[1.5rem] font-semibold leading-[1.1] text-white sm:text-[1.75rem]"
                    style={{ letterSpacing: "-0.03em" }}
                  >
                    {card.title}
                  </span>
                  <span className="inline-flex items-center gap-2.5 text-base font-semibold text-white">
                    {card.cta}
                    <ArrowRight
                      className="h-[17px] w-[17px] transition-transform duration-300 group-hover:translate-x-1.5"
                      strokeWidth={2.4}
                    />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== CLOSING CTA ===== */}
      <section
        className="relative overflow-hidden px-5 py-24 sm:px-8 lg:px-14"
        style={{ background: INK_DEEP }}
      >
        <Reveal
          variants={revealBlur}
          amount={0.4}
          className="relative mx-auto max-w-[1328px]"
        >
          <div className="flex flex-col gap-9">
            <h2
              className="max-w-[900px] font-display text-[2rem] font-semibold leading-[1.06] text-white sm:text-[2.5rem]"
              style={{ letterSpacing: "-0.04em" }}
            >
              {getT("cta.title")}
            </h2>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/rooms"
                className="inline-flex h-14 items-center rounded-full bg-white px-8 text-[16.5px] font-semibold transition-transform duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
                style={{ color: INK_DEEP }}
              >
                {getT("cta.button")}
              </Link>
              <Link
                to="/contact"
                className="inline-flex h-14 items-center rounded-full border-[1.5px] px-7 text-[16.5px] font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10 active:scale-[0.98]"
                style={{ borderColor: "rgba(255,255,255,0.3)" }}
              >
                {getT("cta.secondary")}
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
